import * as React from "react";
import { KeyValue, Query } from "../../interfaces";
import FinancialReportDetailsSearch from "../../containers/FinancialReportDetailsSearchForm";
import { apiPost, fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import { Box, Grid, Typography } from "@mui/material";
import { getReportPeriod } from "../FinancialReporting/FinancialReportingUtilities";
import { removeKeyPrefix } from "../../common/services/Utilities";
import useGetTop100Certs from "../../hooks/useGetTop100Certs";
import {
  ALL_RATIO_FIELDS,
  VARIABLES_TO_SKIP,
  getFilters,
  getSearch,
  sumFieldsLookup,
} from "./PeerGroupUtilities";
import { isEqual } from "lodash";
import { stringify, parse } from "flatted";

interface Props {
  appState: Query;
  updateUi: (filter: string, value: string | string[] | KeyValue[]) => void;
  updateFilters: (filter: string, value: string[]) => void;
  stepTwoCustomData?: KeyValue[];
}

export default function PeerGroupStepTwo({
  appState,
  updateFilters,
  updateUi,
  stepTwoCustomData,
}: Props) {
  const [makeFinancialsCall, setMakeFinancialsCall] = React.useState(false);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [previousReportType, setPreviousReportType] = React.useState<string[]>([
    "",
  ]);
  const [previousUnitType, setPreviousUnitType] = React.useState<string[]>([
    "",
  ]);
  const [previousIncomeBasis, setPreviousIncomeBasis] = React.useState<
    string[]
  >([""]);

  const [peerGroupData, setPeerGroupData] = React.useState<KeyValue[]>([{}]);
  const [peerGroupsInfo, setPeerGroupsInfo] = React.useState<KeyValue[] | null>(
    stepTwoCustomData ? stepTwoCustomData : null,
  );
  const currentPeerGroups = appState.ui.peerGroups;
  const reportType = appState.ui.reportType;
  const [previousReportPeriod, setPreviousReportPeriod] = React.useState<
    string[]
  >([]);
  const [currentFields, setCurrentFields] = React.useState<string[]>([]);
  const [newRepCall, setNewRepCall] = React.useState(true);

  const [dataLoading, setDataLoading] = React.useState(false);
  const top100Certs = useGetTop100Certs(appState, false, setMakeFinancialsCall);
  //gets the fields used in the post request for agg sum based on report type

  const storedData = localStorage.getItem("appStateUi");

  React.useEffect(() => {
    if (appState.ui.peerGroups?.length) {
      localStorage.setItem("appStateUi", stringify(appState.ui));
    }
  }, [appState.ui]);

  //gets the peer group names for the headers and filters used by the financials api call
  React.useEffect(() => {
    if (!peerGroupsInfo && !stepTwoCustomData) {
      let peerGroups: string[] = [];
      if (!currentPeerGroups.length && storedData) {
        const data = parse(storedData);
        peerGroups = data.peerGroups;
        updateUi("peerGroups", peerGroups);
      } else {
        peerGroups = currentPeerGroups;
      }
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/peer_groups?filters=short_name:(${peerGroups.join(
            " OR ",
          )})`,
        )
          .then((response) => {
            const peerGroupNameFilters = peerGroups?.map((groupID: string) => {
              return response.data.find(
                (data: KeyValue) => data.short_name === groupID,
              );
            });
            updateUi(
              "peerGroupNames",
              peerGroupNameFilters.map((value: KeyValue) => {
                return value.name;
              }),
            );
            setPeerGroupsInfo(
              peerGroupNameFilters.map((value: KeyValue) => {
                return {
                  filterCriteria: value.filter_criteria,
                  longName: value.name,
                };
              }),
            );
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
  }, [
    currentPeerGroups,
    peerGroupsInfo,
    stepTwoCustomData,
    storedData,
    updateUi,
  ]);

  React.useEffect(() => {
    const newMaxRepDates: string[] = [];
    const newMinRepDates: string[] = [];
    if (peerGroupsInfo) {
      peerGroupsInfo.forEach((val: KeyValue) => {
        if (val.maxReportDate && val.minReportDate) {
          newMaxRepDates.push(val.maxReportDate);
          newMinRepDates.push(val.minReportDate);
        } else {
          newMaxRepDates.push(appState.ui.latestReportPeriod);
          newMinRepDates.push("19920313");
        }
      });
    }
    if (
      (!appState.ui.maxRepDates ||
        JSON.stringify(newMaxRepDates) !==
          JSON.stringify(appState.ui.maxRepDates)) &&
      initialLoad
    ) {
      updateUi("maxRepDates", newMaxRepDates);
    }
    if (
      (!appState.ui.minRepDates ||
        JSON.stringify(newMinRepDates) !==
          JSON.stringify(appState.ui.minRepDates)) &&
      initialLoad
    ) {
      updateUi("minRepDates", newMinRepDates);
    }
    setInitialLoad(false);
  }, [
    appState.ui.latestReportPeriod,
    appState.ui.maxRepDates,
    appState.ui.minRepDates,
    initialLoad,
    peerGroupsInfo,
    updateUi,
  ]);

  //gets latest report period for the selectable details
  React.useEffect(() => {
    if (
      appState.filters.reportPeriod.length !== appState.ui.peerGroups.length ||
      !appState.filters.reportPeriod[0]
    ) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?limit=1&offset=0&sort_by=RISDATE&sort_order=DESC&fields=RISDATE`,
        )
          .then((response) => {
            const risDateMonth = response.data[0].RISDATE.substring(4, 6);
            const risDateDay = response.data[0].RISDATE.substring(6, 8);
            const risDateYear = response.data[0].RISDATE.substring(0, 4);

            updateFilters(
              "reportPeriod",
              appState.ui.peerGroups.map(
                () => `${risDateYear}${risDateMonth}${risDateDay}`,
              ),
            );

            updateUi(
              "incomeBasis",
              appState.ui.peerGroups.map(() => "YTD"),
            );
            updateUi(
              "unitType",
              appState.ui.peerGroups.map(() => "$"),
            );
          })
          .catch((e: Error) => {
            const currentDate = getReportPeriod(new Date());
            if (!appState.filters.reportPeriod[0]) {
              updateFilters(
                "reportPeriod",
                appState.ui.peerGroups.map(() => currentDate),
              );
            }
          });
      })();
    }
  }, [
    appState.filters.reportPeriod,
    appState.ui.peerGroups,
    updateFilters,
    updateUi,
  ]);

  //resets income basis and unit type after custom results display removed them
  React.useEffect(() => {
    if (
      appState.filters.reportPeriod.length > appState.ui.incomeBasis.length &&
      appState.filters.reportPeriod.length > appState.ui.unitType.length &&
      (appState.ui.reportType !== "custom" ||
        appState.ui.reportType !== "customResults")
    ) {
      const newIncome = appState.filters.reportPeriod.map(
        () => appState.ui.incomeBasis[0],
      );
      const newUnitType = appState.filters.reportPeriod.map(
        () => appState.ui.unitType[0],
      );
      updateUi("incomeBasis", newIncome);
      updateUi("unitType", newUnitType);
    }
  });

  const [fieldsArray, setFieldsArray] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (
      appState.ui.reportStructure?.length &&
      reportType !== previousReportType
    ) {
      const pushFieldsArray = (fields: KeyValue[], fieldArray: string[]) => {
        fields.forEach((fieldVal: KeyValue) => {
          const ytdVal = fieldVal.variable_ytd;
          const ytdValr = fieldVal.variable_ytdr;
          const qtlyVal = fieldVal.variable_qtly;
          const qtlyValr = fieldVal.variable_qtlyr;
          if (ytdVal && ytdVal !== "NAMEFULL" && ytdVal !== "NAMEHCR") {
            fieldArray.push(ytdVal);
          }
          if (
            ytdValr &&
            ytdVal !== ytdValr &&
            ytdValr !== "NAMEFULL" &&
            ytdValr !== "NAMEHCR"
          ) {
            fieldArray.push(ytdValr);
          }
          if (
            qtlyVal &&
            ytdVal !== qtlyVal &&
            qtlyVal !== "NAMEFULL" &&
            qtlyVal !== "NAMEHCR"
          ) {
            fieldArray.push(qtlyVal);
          }
          if (
            qtlyValr &&
            ytdValr !== qtlyValr &&
            qtlyValr !== "NAMEFULL" &&
            qtlyValr !== "NAMEHCR"
          ) {
            fieldArray.push(qtlyValr);
          }
          if (fieldVal.sub_fields) {
            pushFieldsArray(fieldVal.sub_fields, fieldArray);
          }
        });
        return fieldArray;
      };

      const values = pushFieldsArray(
        appState.ui?.reportStructure[0]?.fields,
        [],
      );
      setFieldsArray(values);
    }
  }, [appState.ui.reportStructure, previousReportType, reportType]);
  React.useEffect(() => {
    if (
      (makeFinancialsCall ||
        top100Certs.length === 0 ||
        (fieldsArray.length !== currentFields.length &&
          reportType !== "custom")) &&
      reportType &&
      reportType !== "loadModal"
    ) {
      setMakeFinancialsCall(false);
      const filteredFieldsArray = fieldsArray.filter(
        (field: string) => !["IDT1CER", "IDT1RWAJR"].includes(field) && field,
      );
      const sumFieldsFiltered = filteredFieldsArray.map((val: string) => {
        const sumFieldVariables = sumFieldsLookup[val]
          ? sumFieldsLookup[val].join()
          : val.slice(0, -1);
        return !VARIABLES_TO_SKIP.includes(val) &&
          ALL_RATIO_FIELDS.includes(val)
          ? sumFieldVariables
          : val;
      });

      const combinedAggSum = Array.from(
        new Set([
          ...filteredFieldsArray,
          ...sumFieldsFiltered,
          "ASSET",
          "ASSETR",
          "ASSET5",
          "ASSET2",
        ]),
      );
      const financialsPostReq = (
        index: number,
        groupInfo: KeyValue[],
        repVal: string,
      ) => {
        return apiPost({
          limit: 1,
          offset: 0,
          filters: getFilters(index, groupInfo, repVal, top100Certs),
          search: getSearch(index, groupInfo),
          agg_by: "RISDATE",
          agg_sum_fields: combinedAggSum.join(),
          pgc: true,
          report_type: appState.ui.reportType,
          report_period: appState.filters.reportPeriod[index],
        });
      };

      //called when report type is changed
      if (
        fieldsArray.length !== currentFields.length &&
        reportType !== "custom" &&
        peerGroupsInfo
      ) {
        setDataLoading(true);
        setCurrentFields(fieldsArray);

        const tier1Url =
          fieldsArray.includes("IDT1CER") || fieldsArray.includes("IDT1RWAJR")
            ? appState.filters.reportPeriod.map(
                (reportValue: string, index: number) =>
                  fetchData(`${ApiBaseUrl()}/financials?filters=${getFilters(
                    index,
                    peerGroupsInfo,
                    reportValue,
                    top100Certs,
                  )} AND INSFDIC:1 AND CBLRIND:0 AND IBA:0&limit=1&agg_by=RISDATE&agg_sum_fields=RBCT1C,RWAJ,RBCT1J
            `),
              )
            : [];

        const fetchUrls = [
          ...appState.filters.reportPeriod.map(
            (reportValue: string, index: number) =>
              fetchData(
                `${ApiBaseUrl()}/financials`,
                financialsPostReq(index, peerGroupsInfo, reportValue),
              ),
          ),
          ...tier1Url,
        ];
        (async () => {
          await Promise.all(fetchUrls)
            .then((response) => {
              if (
                tier1Url.length > 0 &&
                tier1Url.length === fetchUrls.length / 2
              ) {
                const halfResponse = Math.ceil(response.length / 2);
                const postRes = response.slice(0, halfResponse);
                const tier1 = response.slice(halfResponse);
                const combinedResponse = postRes.map(
                  (data: KeyValue, int: number) => {
                    const tier1Data = {
                      sum_IDT1CER: tier1[int].totals.sum_RWAJ
                        ? (tier1[int].totals.sum_RBCT1C /
                            tier1[int].totals.sum_RWAJ) *
                          100
                        : undefined,
                      sum_IDT1RWAJR: tier1[int].totals.sum_RWAJ
                        ? (tier1[int].totals.sum_RBCT1J /
                            tier1[int].totals.sum_RWAJ) *
                          100
                        : undefined,
                    };
                    return { ...data.totals, ...tier1Data };
                  },
                );
                const data = combinedResponse.map((res: KeyValue) => {
                  return removeKeyPrefix(res, "sum_");
                });
                setPeerGroupData(data);
              } else {
                const data = response.map((res: any) => {
                  return removeKeyPrefix(res.totals, "sum_");
                });
                setPeerGroupData(data);
              }
            })
            .catch((error: Error) => {
              console.error(`${error.name} : ${error.message}`);
            })
            .finally(() => {
              setPreviousReportType(reportType);
              setDataLoading(false);
            });
        })();
      }
      //called single report change
      else if (
        peerGroupsInfo &&
        peerGroupData &&
        reportType !== "custom" &&
        (JSON.stringify(previousReportPeriod) !==
          JSON.stringify(appState.filters.reportPeriod) ||
          JSON.stringify(previousUnitType) !==
            JSON.stringify(appState.ui.unitType) ||
          JSON.stringify(previousIncomeBasis) !==
            JSON.stringify(appState.ui.incomeBasis))
      ) {
        setDataLoading(true);

        const changedIndices: number[] = appState.filters.reportPeriod
          .map((_: string, index: number) => index)
          .filter(
            (index: number) =>
              previousReportPeriod[index] !==
                appState.filters.reportPeriod[index] ||
              previousUnitType[index] !== appState.ui.unitType[index] ||
              previousIncomeBasis[index] !== appState.ui.incomeBasis[index],
          );

        setPreviousReportPeriod(appState.filters.reportPeriod);
        setPreviousUnitType(appState.ui.unitType);
        setPreviousIncomeBasis(appState.ui.incomeBasis);

        if (
          previousReportPeriod.length > 0 &&
          reportType === previousReportType
        ) {
          const hasTier1Fields =
            fieldsArray.includes("IDT1CER") ||
            fieldsArray.includes("IDT1RWAJR");
          const fetchUrls = changedIndices?.map((i: number) => {
            const reportValue = appState.filters.reportPeriod[i];
            const mainFetch = fetchData(
              `${ApiBaseUrl()}/financials`,
              financialsPostReq(i, peerGroupsInfo, reportValue),
            );

            if (hasTier1Fields) {
              const tier1Fetch = fetchData(
                `${ApiBaseUrl()}/financials?filters=${getFilters(
                  i,
                  peerGroupsInfo,
                  reportValue,
                  top100Certs,
                )} AND INSFDIC:1 AND CBLRIND:0 AND IBA:0&limit=1&agg_by=RISDATE&agg_sum_fields=RBCT1C,RWAJ,RBCT1J`,
              );
              return Promise.all([mainFetch, tier1Fetch]).then(
                ([mainRes, tier1Res]) => {
                  const tier1Data = {
                    sum_IDT1CER: tier1Res.totals.sum_RWAJ
                      ? (tier1Res.totals.sum_RBCT1C /
                          tier1Res.totals.sum_RWAJ) *
                        100
                      : undefined,
                    sum_IDT1RWAJR: tier1Res.totals.sum_RWAJ
                      ? (tier1Res.totals.sum_RBCT1J /
                          tier1Res.totals.sum_RWAJ) *
                        100
                      : undefined,
                  };
                  return {
                    i,
                    data: removeKeyPrefix(
                      { ...mainRes.totals, ...tier1Data },
                      "sum_",
                    ),
                  };
                },
              );
            }

            return mainFetch.then((res: any) => ({
              i,
              data: removeKeyPrefix(res.totals, "sum_"),
            }));
          });

          (async () => {
            await Promise.all(fetchUrls)
              .then((results) => {
                const newData = peerGroupData?.map(
                  (existingData: KeyValue, index: number) => {
                    const updated = results.find(
                      (r: { i: number; data: KeyValue }) => r.i === index,
                    );
                    return updated ? updated.data : existingData;
                  },
                );
                setPeerGroupData(newData);
              })
              .catch((error) => {
                console.error(error);
              })
              .finally(() => {
                reportType === previousReportType && setDataLoading(false);
              });
          })();
        }
      }
    }
  }, [
    appState.filters.reportPeriod,
    appState.ui.incomeBasis,
    appState.ui.reportType,
    appState.ui.unitType,
    currentFields.length,
    fieldsArray,
    makeFinancialsCall,
    peerGroupData,
    peerGroupsInfo,
    previousIncomeBasis,
    previousReportPeriod,
    previousReportType,
    previousUnitType,
    reportType,
    top100Certs,
  ]);

  const peerGroupHeader = (
    <Box
      sx={{
        width: "fit-content",
        minWidth: "100%",
      }}
    >
      <Grid container sx={{ flexWrap: "nowrap" }}>
        <Grid
          sx={{
            "minWidth": 477,
            "position": "sticky",
            "left": 0,
            "zIndex": 4,
            "backgroundColor": "#FFFFFF",
            "&.MuiGrid-root": {
              padding: "16px 60px 8px 0px",
            },
            "& h2": {
              paddingBottom: ".5rem",
            },
          }}
          size={{
            xs: 12,
            md: 8,
          }}
        >
          <Typography variant="h2">
            Step {appState.ui.comparisonType !== "" ? "2" : "3"}
          </Typography>
          <Typography
            variant="body2"
            color="primary.main"
            style={{ fontWeight: 800 }}
          >
            Choose “what” data you wish to compare. This defines the rows of
            your report.
          </Typography>
        </Grid>
        {appState.ui.peerGroups.map((pgcVal: string, index: number) => {
          return (
            <Grid
              id={`peer_group_column_${index}`}
              key={`${pgcVal}_${index}`}
              style={{
                minWidth: 236,
                zIndex: 3,
                paddingTop: 32,
                marginLeft: index === 0 ? 16 : 0,
                backgroundColor: index % 2 === 0 ? "#ede7d3" : "#f5f3e9",
              }}
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <Typography
                align="center"
                variant="body1"
                color="primary.main"
                sx={{ fontWeight: "bold", paddingBottom: "0.5rem" }}
              >{`Column ${index + 1} - ${
                peerGroupsInfo && peerGroupsInfo[index].title
                  ? peerGroupsInfo[index].title
                  : "Peer Group"
              }`}</Typography>
              <Typography
                align="center"
                variant="body2"
                color="primary.main"
                sx={{ fontWeight: "bold", paddingBottom: 0 }}
              >
                {peerGroupsInfo && peerGroupsInfo[index].longName}
              </Typography>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  //this is needed if the sign-in status changes
  React.useEffect(() => {
    if (stepTwoCustomData && !isEqual(stepTwoCustomData, peerGroupsInfo)) {
      setPeerGroupsInfo(stepTwoCustomData);
    }
  }, [peerGroupsInfo, stepTwoCustomData]);

  React.useEffect(() => {
    if (appState.ui.customVariables?.length > 0) {
      setNewRepCall(true);
    }
  }, [appState.ui.customVariables?.length]);

  React.useEffect(() => {
    const newRepPeriod =
      stepTwoCustomData &&
      peerGroupsInfo &&
      newRepCall &&
      appState.filters?.reportPeriod?.[0]
        ? peerGroupsInfo
            ?.map((data) => {
              return (
                data.appState?.reportPeriod ??
                data.appState.filters?.reportPeriod?.[0]
              );
            })
            .filter((val: string) => val !== "")
        : [""];
    if (
      newRepPeriod[0] !== "" &&
      newRepPeriod.length === appState.filters.reportPeriod.length &&
      JSON.stringify(newRepPeriod) !==
        JSON.stringify(appState.filters.reportPeriod)
    ) {
      updateFilters("reportPeriod", newRepPeriod);
      setNewRepCall(false);
    }
  }, [
    appState,
    appState.filters.reportPeriod,
    newRepCall,
    peerGroupsInfo,
    stepTwoCustomData,
    updateFilters,
  ]);
  return (
    <FinancialReportDetailsSearch
      formHeader={peerGroupHeader}
      dataLoading={dataLoading}
      data={peerGroupData}
      fromLevelOne={false}
      peerGroupsInfo={peerGroupsInfo}
    />
  );
}
